import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer } from 'react';
// utils
import {CognitoHostedUIIdentityProvider} from "@aws-amplify/auth";
import Amplify, { Auth, Hub } from 'aws-amplify';
import { isValidToken, setSession } from '../utils/jwt';
import axios from '../utils/axios';
import amplifyConfig from '../amplifyConfig';
import {
  logoutUser,
  setIsAuthenticated,
  setIsInitialized,
  setIsRegistered, setToken, setUserAdditionalInfo, setUserEmail,
  setUserInfo, setUserRegisterData,
  setUserRole,
  setAudit
} from "../redux/slices/user";
import {useDispatch, useSelector} from '../redux/store';
import moment from "moment";
import localStorage from "redux-persist/es/storage";
import {setPages} from "../redux/slices/adminPages";
import { BLOCKED_ROLE_UUID, REGISTER_ROLE_UUID } from 'src/constants/user-role-constants';
import { isAuthorizedEmailDomain } from '../utils/email-domain-validator';

Amplify.configure(amplifyConfig);

// ----------------------------------------------------------------------


const AuthContext = createContext({});

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {
  const {isInitialized, email, token} = useSelector((state) => state.user);
  const reduxDispatch = useDispatch();

  useEffect(() => {
    if (!axios.defaults.headers.common.Authorization && token) {
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    }
    const initialize = async () => {
      try {
        const documentReferrer = sessionStorage.getItem("documentReferrer")
        if (!documentReferrer && document.referrer && document.referrer.indexOf("e2f.io") < 0 && document.referrer.indexOf("jobs.trustscale.ai") < 0) {
          sessionStorage.setItem("documentReferrer", document.referrer)
        }
        const isSourceRecorded = sessionStorage.getItem("isSourceRecorded")
        if (email && isSourceRecorded !== "true" && sessionStorage.getItem("documentReferrer")) {
          sessionStorage.setItem("isSourceRecorded", "true")
          // record source
          await axios.post("/trackSource", {
            email: email,
            source: sessionStorage.getItem("documentReferrer")
          })
        }
        const tokenTime = await localStorage.getItem("tokenCreatedTime")
        if ((moment(moment.now()).diff(parseFloat(tokenTime), "hours") >= 23 || !tokenTime) && !window.location.href.includes("invitation")) {
          googleSignOut()
        } else {
          if(!isInitialized || !email || !token || !tokenTime) {
            await getUser().then(async userData => {
              if (userData) {
                const loginResponse = await audit(userData);
                if (!loginResponse) {
                  googleSignOut()
                } else {
                  let _token = userData.signInUserSession.idToken.jwtToken
                  if (loginResponse.accessToken) {
                    _token = loginResponse.accessToken
                  }
                  reduxDispatch(setToken(_token))
                  localStorage.setItem("tokenCreatedTime", moment.now().toString())
                  await isUserRegistered(userData?.signInUserSession?.idToken?.payload?.email, userData, loginResponse)
                  setSession(_token);
                }
              } else {
                reduxDispatch(setIsInitialized(true))
              }
            });
          }
        }
        reduxDispatch(setIsInitialized(true))
      } catch (err) {
        console.error(err);
      }
    };

    initialize();
  }, []);

  async function getUser() {
    try {
      const userData = await Auth.currentAuthenticatedUser();
      return userData;
    } catch {
      if (window.location.href.includes("loading")) {
        sessionStorage.removeItem("documentReferrer")
        sessionStorage.removeItem("isSourceRecorded")
        logout()
        window.location.href = "/"
      }
      return console.log('Not signed in');
    }
  }

  const audit = async (userData)=> {
    axios.defaults.headers.common.Authorization = `Bearer ${userData.signInUserSession.idToken.jwtToken}`;
    let data;
    await axios.post('/login', {
      type: "login",
      name: userData?.signInUserSession?.idToken?.payload?.name || userData?.signInUserSession?.idToken?.payload?.email, //serdar: added OR for email/pwd authenticated user of e2e tests which doesn't have "name" in payload
      email: userData?.signInUserSession?.idToken?.payload?.email,
    }).then((res) => {
      if (typeof res.data === 'string') {
        reduxDispatch(setUserRole(BLOCKED_ROLE_UUID))
        localStorage.setItem("userBlocked", "1")
        return false
      } else {
        reduxDispatch(setAudit({ip: res.data.auditStatus.ip,
          registerStatus: res.data.auditStatus.registerStatus,
          loginStatus: res.data.auditStatus.loginStatus,
          createdAt: res.data.auditStatus.createdAt  }))
        data = res.data;
      }
    });
    return data
  }

  const isUserRegistered = async (userEmail, userData, data) => {
    if (isAuthorizedEmailDomain(userData?.signInUserSession?.idToken?.payload?.email)) {
      reduxDispatch(setPages(configureAdminPagesData(data.rules || [])))
    }
    if (data.auditStatus.loginStatus === "false") {
      reduxDispatch(setIsRegistered(false))
      reduxDispatch(setIsAuthenticated(true))
      reduxDispatch(setIsInitialized(true))
      reduxDispatch(setUserEmail({email: userData?.signInUserSession?.idToken?.payload?.email}))
      reduxDispatch(setUserInfo({
        name: userData?.signInUserSession?.idToken?.payload?.name.substring(0, userData?.signInUserSession?.idToken?.payload?.name.lastIndexOf(" ")),
        surname: userData?.signInUserSession?.idToken?.payload?.name.substring(userData?.signInUserSession?.idToken?.payload?.name.lastIndexOf(" ") + 1)
      }))
      reduxDispatch(setUserRole(REGISTER_ROLE_UUID))
    } else if(data.auditStatus.loginStatus === "true") {
      reduxDispatch(setUserRegisterData(data.user))
    }
  };
  const configureAdminPagesData = (rules) => {
    const _arr = []
    _arr.push("Profile")

    for (let i = 0; i < rules.length; i++) {
      if(i==0){
        _arr.push("Add Remove Domains")
      }
      if (rules[i].service === "job") {
        if (rules[i].rights?.includes("Read")) {
          _arr.push("Job Type List")
        }
        if (rules[i].rights?.includes("Write")) {
          _arr.push("Create Job Type")
        }
      }
      if (rules[i].service === "question") {
        if (rules[i].rights?.includes("Read")) {
          _arr.push("Browse Questions")
        }
        if (rules[i].rights?.includes("Write")) {
          _arr.push("Create Question")
          _arr.push("Upload Questions")
        }
      }
      if (rules[i].service === "test") {
        if (rules[i].rights?.includes("Read")) {
          _arr.push("Browse Tests")
          _arr.push("User's Test Results")
        }
        if (rules[i].rights?.includes("Write")) {
          _arr.push("Create Test")
        }
      }
      if (rules[i].service === "testEvaluation") {
        if (rules[i].rights?.includes("Read")) {
          _arr.push("Evaluate Language Skills Tests")
        }
      }
      if (rules[i].service === "voiceCollection") {
        if (rules[i].rights?.includes("Write")) {
          _arr.push("Voice Collection")
          _arr.push("Voice Collection Evaluation")

        }
        if (rules[i].rights?.includes("Read")) {
          _arr.push("Browse Voice Collection Form")

        }
      }
      if (rules[i].service === "adminSettings") {
        if (rules[i].rights?.includes("Read") || rules[i].rights?.includes("Write")) {
          _arr.push("Admin Settings")
        }
      }
      if (rules[i].service === "blockUser") {
        if (rules[i].rights?.includes("Read") || rules[i].rights?.includes("Write")) {
          _arr.push("Block Users")
        }
      }
      if (rules[i].service === "reportUser") {
        if (rules[i].rights?.includes("Read") || rules[i].rights?.includes("Write")) {
          _arr.push("User List")
        }
      }
      if (rules[i].service === "authSettings") {
        if (rules[i].rights?.includes("Read") || rules[i].rights?.includes("Write")) {
          _arr.push("Auth Settings")
        }
      }
    }
    return _arr
  }

  const googleSignIn = () => {
    Auth.federatedSignIn({provider: CognitoHostedUIIdentityProvider.Google }).then(res => {
      localStorage.setItem("tokenCreatedTime", moment.now().toString())
    })
  }

  const googleSignOut = () => {
    sessionStorage.removeItem("documentReferrer")
    sessionStorage.removeItem("isSourceRecorded")
    logout()
    Auth.signOut().then(res => {
      console.log(res)
    })
  }

  const logout = () => {
    setSession(null);
    reduxDispatch(logoutUser())
  };

  return (
      <AuthContext.Provider
          value={{
            method: 'jwt',
            logout,
            googleSignIn,
            googleSignOut,
            isUserRegistered,
          }}
      >
        {children}
      </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };